import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import CategoryOwnerAvatar from './CategoryOwnerAvatar';
import { Download, Users, Search, Loader2, Check } from 'lucide-react';
import { toast } from '../../components/ui/use-toast';
import favoritesService from '../../services/favoritesService';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { SOP } from '@/types/sop';
import PublicCategoryPreviewDialog from './PublicCategoryPreviewDialog';
import { Skeleton } from '../../components/ui/skeleton';
import { useAuth } from '../../hooks/useAuth';

interface PublicCategory {
  id: string;
  name: string;
  userId: string;
  sopIds: string[];
  isPublic?: boolean;
  originalOwner?: {
    userId: string;
    name: string;
    displayName?: string;
    profilePic?: string;
  };
  creatorInfo?: {
    name: string;
    displayName: string;
    profilePic?: string;
  };
}

interface PublicCategoriesListProps {
  onCategoryImported: () => void;
}

const PublicCategoriesList: React.FC<PublicCategoriesListProps> = ({ onCategoryImported }) => {
  const { user, displayName, profilePictureUrl } = useAuth();
  const [publicCategories, setPublicCategories] = useState<PublicCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<PublicCategory | null>(null);
  const [showSOPsDialog, setShowSOPsDialog] = useState(false);
  const [categorySOPs, setCategorySOPs] = useState<SOP[]>([]);
  const [loadingSOPs, setLoadingSOPs] = useState(false);
  const [importedCategories, setImportedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchPublicCategories = async () => {
      try {
        setLoading(true);
        const categories = await favoritesService.getPublicCategories();
        
        // Show all public categories, including the user's own
        const filteredCategories = categories.filter(
          category => category.isPublic === true
        );
        
        setPublicCategories(filteredCategories);
      } catch (error) {
        console.error('Error fetching public categories:', error);
        toast({
          title: "Error",
          description: "Failed to load public categories",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchPublicCategories();
    }
  }, [user]);

  // Check which categories have already been imported
  useEffect(() => {
    const checkImportedCategories = async () => {
      if (!user) return;
      
      try {
        const userCategories = await favoritesService.getCategories(user.uid);
        const imported = new Set(
          userCategories
            .filter(cat => cat.originalOwner)
            .map(cat => cat.originalOwner?.userId + '_' + cat.name)
        );
        setImportedCategories(imported);
      } catch (error) {
        console.error('Error checking imported categories:', error);
      }
    };

    checkImportedCategories();
  }, [user]);

  const handleImportCategory = async (category: PublicCategory) => {
    if (!user) return;
    
    try {
      setImportingId(category.id);
      
      // Pass the current user's info for proper handling in importCategory
      const userName = displayName || user.displayName || user.email || 'Unknown User';
      const profilePic = profilePictureUrl || user.photoURL || undefined;
      
      await favoritesService.importCategory(
        category.id, 
        user.uid, 
        userName, 
        profilePic
      );
      
      toast({
        title: "Success",
        description: `Imported "${category.name}" to your favorites`,
      });
      
      onCategoryImported();
    } catch (error) {
      console.error('Error importing category:', error);
      toast({
        title: "Error",
        description: "Failed to import category",
        variant: "destructive",
      });
    } finally {
      setImportingId(null);
    }
  };

  const handleViewSOPs = async (category: PublicCategory) => {
    setSelectedCategory(category);
    setShowSOPsDialog(true);
    setLoadingSOPs(true);

    try {
      const sopsData: SOP[] = [];
      
      // Fetch each SOP individually
      await Promise.all(category.sopIds.map(async (sopId) => {
        try {
          const sopDoc = await getDoc(doc(db, 'sops', sopId));
          if (sopDoc.exists()) {
            sopsData.push(sopDoc.data() as SOP);
          }
        } catch (error) {
          console.error('Error fetching SOP:', sopId, error);
        }
      }));

      setCategorySOPs(sopsData);
    } catch (error) {
      console.error('Error loading category SOPs:', error);
      toast({
        title: "Error",
        description: "Failed to load SOPs",
        variant: "destructive",
      });
    } finally {
      setLoadingSOPs(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center justify-between p-4 border rounded-md">
            <div className="flex items-center space-x-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <Skeleton className="h-9 w-24" />
          </div>
        ))}
      </div>
    );
  }

  if (publicCategories.length === 0) {
    return (
      <div className="text-center py-8 text-gray-600 dark:text-gray-400">
        <Users className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-600" />
        <p>No public categories available yet.</p>
        <p className="text-sm mt-2">When other users make their categories public, they'll appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {publicCategories.map((category) => (
        <div 
          key={category.id} 
          className="flex items-center justify-between p-4 border rounded-md bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 favorites-category-item"
        >
          <div className="flex items-center space-x-4">
            <CategoryOwnerAvatar
              profilePic={category.creatorInfo?.profilePic}
              name={category.creatorInfo?.displayName || category.creatorInfo?.name || 'Unknown User'}
              size="sm"
            />
            <div>
              <h3 
                className="font-medium text-gray-900 dark:text-white hover:text-green-600 dark:hover:text-green-400 cursor-pointer"
                onClick={() => handleViewSOPs(category)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleViewSOPs(category);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {category.name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                By: {category.creatorInfo?.displayName || category.creatorInfo?.name || 'Unknown User'} • {category.sopIds.length} SOPs
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleViewSOPs(category)}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {loadingSOPs && selectedCategory?.id === category.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              <span>View</span>
            </Button>
            {category.userId === user?.uid ? (
              <span className="px-2 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-700 dark:bg-blue-700 dark:text-blue-100 ring-1 ring-inset ring-blue-700/10">
                Owned by you
              </span>
            ) : importedCategories.has(category.userId + '_' + category.name) ? (
              <Button
                variant="outline"
                size="sm"
                disabled
                className="flex items-center gap-1 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
              >
                <Check className="h-4 w-4" />
                <span>Imported</span>
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleImportCategory(category)}
                disabled={importingId === category.id} // Removed category.userId === user?.uid check here
                className="flex items-center gap-1 bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                {importingId === category.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span>Import</span>
              </Button>
            )}
          </div>
        </div>
      ))}
      {selectedCategory && (
        <PublicCategoryPreviewDialog
          isOpen={showSOPsDialog}
          onClose={() => setShowSOPsDialog(false)}
          categoryName={selectedCategory.name}
          sops={categorySOPs}
        />
      )}
    </div>
  );
};

export default PublicCategoriesList;